<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import AppShell from '@/components/AppShell.vue'
import DashboardFunnelCard from '@/components/DashboardFunnelCard.vue'
import DashboardAccountHealthCard from '@/components/DashboardAccountHealthCard.vue'
import DashboardNeedsAttentionFeed from '@/components/DashboardNeedsAttentionFeed.vue'
import ExportFreshnessNudge from '@/components/ExportFreshnessNudge.vue'
import type { DashboardView } from '@/types/models'

const auth = useAuthStore()
const store = useDashboardStore()
const router = useRouter()

const accountsDueForFreshnessNudge = computed(() =>
  store.accountHealth.filter((entry) => entry.export_freshness_nudge_due),
)

const showEmptyState = computed(
  () => store.accountHealth.length === 0 || store.campaignFunnels.length === 0,
)

function goToConnectLinkedin() {
  router.push({ name: 'connect-linkedin' })
}

function goToNewCampaign() {
  router.push({ name: 'new-campaign' })
}

async function load() {
  const workspaceId = auth.activeWorkspaceId
  if (!workspaceId) return
  await store.fetchDashboard(workspaceId)
}

async function switchView(next: DashboardView) {
  if (store.view === next) return
  store.setView(next)
  await load()
}

async function handleSnooze(linkedinAccountId: string) {
  const workspaceId = auth.activeWorkspaceId
  if (!workspaceId) return
  try {
    await store.snoozeExportFreshness(workspaceId, linkedinAccountId)
  } catch {
    // store.snoozeError already holds a user-facing message; nothing further to do here.
  }
}

onMounted(load)
watch(() => auth.activeWorkspaceId, load)
</script>

<template>
  <AppShell title="Dashboard">
    <div class="max-w-container-max space-y-section-gap">
      <div
        v-if="!auth.activeWorkspace"
        class="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8"
      >
        <h2 class="mb-2 font-serif text-xl text-on-surface">
          Welcome, {{ auth.user?.name }}
        </h2>
        <p class="text-body-reading text-on-surface-variant">
          Create a workspace to get started.
        </p>
      </div>

      <template v-else>
        <div class="flex items-center justify-between">
          <div>
            <h2 class="font-serif text-xl font-medium text-on-surface">
              {{ auth.activeWorkspace.name }}
            </h2>
            <p class="mt-1 text-ui-label text-on-surface-variant">
              What's running, what's working, what needs you.
            </p>
          </div>

          <SegmentedControl
            :model-value="store.view"
            :options="[
              { label: 'My accounts', value: 'mine' },
              { label: 'Whole workspace', value: 'workspace' },
            ]"
            @update:model-value="(v: string | number) => switchView(v as DashboardView)"
          />
        </div>

        <p
          v-if="store.error"
          class="text-ui-label text-status-error"
        >
          {{ store.error }}
        </p>
        <p
          v-if="store.snoozeError"
          class="text-ui-label text-status-error"
        >
          {{ store.snoozeError }}
        </p>

        <div
          v-if="store.loading && store.campaignFunnels.length === 0"
          class="text-ui-label text-on-surface-variant"
        >
          Loading dashboard…
        </div>

        <template v-else>
          <div
            v-if="accountsDueForFreshnessNudge.length"
            class="space-y-3"
          >
            <ExportFreshnessNudge
              v-for="account in accountsDueForFreshnessNudge"
              :key="account.linkedin_account_id"
              :account="account"
              :snoozing="store.snoozingAccountId === account.linkedin_account_id"
              @snooze="handleSnooze"
            />
          </div>

          <div
            v-if="showEmptyState"
            class="flex flex-1 min-h-[60vh] items-center justify-center p-8"
          >
            <div class="mx-auto flex max-w-md flex-col items-center space-y-6 text-center">
              <div class="flex h-16 w-16 items-center justify-center rounded-full bg-surface-container-low">
                <span
                  class="material-symbols-outlined text-[32px] text-outline"
                  style="font-variation-settings: 'wght' 200"
                  aria-hidden="true"
                >draft</span>
              </div>

              <h3 class="font-serif text-[28px] font-semibold tracking-tight text-on-surface">
                Nothing here yet
              </h3>

              <p class="text-body-reading text-on-surface-variant">
                Your campaign activity, messages sent, and replies will appear here once you've
                connected a LinkedIn account and launched your first campaign.
              </p>

              <div class="flex w-full flex-col gap-4 pt-4 sm:flex-row sm:justify-center">
                <Button
                  class="w-full sm:w-auto"
                  @click="goToConnectLinkedin"
                >
                  Connect LinkedIn
                </Button>
                <Button
                  variant="secondary"
                  class="w-full sm:w-auto"
                  @click="goToNewCampaign"
                >
                  Create a campaign
                </Button>
              </div>
            </div>
          </div>

          <template v-else>
            <section>
              <h3 class="mb-6 text-headline-md text-on-surface">
                Workspace accounts
              </h3>
              <div class="flex flex-wrap gap-4">
                <DashboardAccountHealthCard
                  v-for="account in store.accountHealth"
                  :key="account.linkedin_account_id"
                  :account="account"
                />
              </div>
            </section>

            <div class="grid grid-cols-1 gap-gutter lg:grid-cols-3">
              <section class="order-2 lg:order-1 lg:col-span-1">
                <h3 class="mb-6 text-headline-md text-on-surface">
                  Needs attention
                </h3>
                <DashboardNeedsAttentionFeed
                  v-if="store.needsAttention"
                  :needs-attention="store.needsAttention"
                />
              </section>

              <section class="order-1 lg:order-2 lg:col-span-2">
                <h3 class="mb-6 text-headline-md text-on-surface">
                  Active campaigns
                </h3>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DashboardFunnelCard
                    v-for="funnel in store.campaignFunnels"
                    :key="funnel.campaign_id"
                    :funnel="funnel"
                  />
                </div>
              </section>
            </div>
          </template>
        </template>
      </template>
    </div>
  </AppShell>
</template>
